import React, { useEffect, useRef, useState } from "react";
import TimeSelect from "components/admin/TimeSelect";
import * as S from "./AdminFacilityDrawer.styled";
import { adminFacilityService } from "services/adminFacilityService";
import { adminBuildingService } from "services/adminBuildingService";
import { adminThemeService } from "services/adminThemeService";
import { Building, Theme } from "types";

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  { value: "", label: "선택하세요" },
  { value: "STUDY", label: "스터디" },
  { value: "SPORTS", label: "체육시설" },
  { value: "CULTURE", label: "문화공간" },
  { value: "PRINT_PC", label: "프린터/PC" },
];

interface OpTime {
  name: string;
  startTime: string;
  endTime: string;
}

interface AvailableTime {
  startTime: string;
  endTime: string;
}

interface FormErrors {
  name?: string;
  category?: string;
  thumbnail?: string;
}

const toTimeString = (hhmm: string) => (hhmm ? `${hhmm}:00` : "");

const AdminFacilityDrawer: React.FC<Props> = ({ onClose, onSuccess }) => {
  /* ── 기본 필드 ── */
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [purpose, setPurpose] = useState("");
  const [equipment, setEquipment] = useState("");
  const [caution, setCaution] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");

  /* ── 이미지 ── */
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const extraInputRef = useRef<HTMLInputElement>(null);

  /* ── 운영 시간 ── */
  const [opTimes, setOpTimes] = useState<OpTime[]>([
    { name: "", startTime: "", endTime: "" },
  ]);

  /* ── 예약 정책 ── */
  const [reservable, setReservable] = useState(false);
  const [availableTimes, setAvailableTimes] = useState<AvailableTime[]>([]);

  /* ── 건물 / 테마 ── */
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [buildingId, setBuildingId] = useState<number>(0);
  const [themeIds, setThemeIds] = useState<number[]>([]);

  /* ── 제출 ── */
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    const universityId = Number(localStorage.getItem("adminUniversityId"));
    adminBuildingService.getBuildings(universityId).then(setBuildings).catch(() => {});
    adminThemeService.getThemes(universityId).then(setThemes).catch(() => {});
  }, []);

  /* ── 운영 시간 helpers ── */
  const setOpTime = (i: number, key: keyof OpTime, value: string) =>
    setOpTimes((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, [key]: value } : t))
    );
  const addOpTime = () =>
    setOpTimes((prev) => [...prev, { name: "", startTime: "", endTime: "" }]);
  const removeOpTime = (i: number) =>
    setOpTimes((prev) => prev.filter((_, idx) => idx !== i));

  /* ── 추가 이미지 helpers ── */
  const handleExtraFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    setExtraFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };
  const removeExtraFile = (i: number) =>
    setExtraFiles((prev) => prev.filter((_, idx) => idx !== i));

  /* ── 예약 가능 시간 helpers ── */
  const addAvailableTime = () =>
    setAvailableTimes((prev) => [...prev, { startTime: "", endTime: "" }]);
  const removeAvailableTime = (i: number) =>
    setAvailableTimes((prev) => prev.filter((_, idx) => idx !== i));
  const setAvailableTime = (i: number, key: keyof AvailableTime, value: string) =>
    setAvailableTimes((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, [key]: value } : t))
    );

  /* ── 테마 토글 ── */
  const toggleTheme = (id: number) =>
    setThemeIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );

  /* ── 유효성 검사 ── */
  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!name.trim()) next.name = "시설명을 입력해주세요.";
    if (!category) next.category = "카테고리를 선택해주세요.";
    if (!thumbnailFile) next.thumbnail = "썸네일 이미지를 선택해주세요.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* ── 제출 ── */
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError("");

    try {
      // 1. 썸네일 업로드
      const thumbnailUrl = await adminFacilityService.uploadImage(thumbnailFile!);

      // 2. 추가 이미지 업로드 (병렬)
      const imageUrls = await Promise.all(
        extraFiles.map((f) => adminFacilityService.uploadImage(f))
      );

      // 3. 시설 생성
      const universityId = Number(localStorage.getItem("adminUniversityId"));
      await adminFacilityService.createFacility(universityId, {
        name: name.trim(),
        description: description.trim(),
        purpose: purpose.trim(),
        equipment: equipment.trim(),
        caution: caution.trim(),
        location: location.trim(),
        thumbnailImage: thumbnailUrl,
        category,
        operationTimes: opTimes
          .filter((t) => t.name.trim())
          .map((t) => ({
            name: t.name.trim(),
            startTime: toTimeString(t.startTime),
            endTime: toTimeString(t.endTime),
          })),
        images: imageUrls,
        reservationPolicy: {
          reservable,
          availableTimes: availableTimes
            .filter((t) => t.startTime && t.endTime)
            .map((t) => ({
              startTime: toTimeString(t.startTime),
              endTime: toTimeString(t.endTime),
            })),
        },
        buildingId,
        themeIds,
      });

      onSuccess();
    } catch {
      setSubmitError("시설 추가에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Drawer onClick={(e) => e.stopPropagation()}>

        {/* 헤더 */}
        <S.DrawerHeader>
          <S.DrawerTitle>시설 추가</S.DrawerTitle>
          <S.CloseButton onClick={onClose}>✕</S.CloseButton>
        </S.DrawerHeader>

        {/* 바디 */}
        <S.DrawerBody>

          {/* 시설명 */}
          <S.Field>
            <S.Label>시설명 <S.Required>*</S.Required></S.Label>
            <S.Input
              placeholder="예) 중앙 스터디룸 A"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors((p) => ({ ...p, name: undefined })); }}
              hasError={!!errors.name}
            />
            {errors.name && <S.FieldError>{errors.name}</S.FieldError>}
          </S.Field>

          {/* 카테고리 */}
          <S.Field>
            <S.Label>카테고리 <S.Required>*</S.Required></S.Label>
            <S.Select
              value={category}
              onChange={(e) => { setCategory(e.target.value); setErrors((p) => ({ ...p, category: undefined })); }}
              hasError={!!errors.category}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </S.Select>
            {errors.category && <S.FieldError>{errors.category}</S.FieldError>}
          </S.Field>

          {/* 위치 */}
          <S.Field>
            <S.Label>위치</S.Label>
            <S.Input
              placeholder="예) 중앙도서관 3층"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </S.Field>

          {/* 설명 */}
          <S.Field>
            <S.Label>설명</S.Label>
            <S.Textarea
              rows={3}
              placeholder="시설에 대한 설명을 입력하세요."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </S.Field>

          {/* 목적 */}
          <S.Field>
            <S.Label>목적</S.Label>
            <S.Input
              placeholder="예) 개인 학습 및 소그룹 스터디"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </S.Field>

          {/* 비품 */}
          <S.Field>
            <S.Label>비품</S.Label>
            <S.Input
              placeholder="예) 화이트보드, 프로젝터, 전원 콘센트"
              value={equipment}
              onChange={(e) => setEquipment(e.target.value)}
            />
          </S.Field>

          {/* 주의사항 */}
          <S.Field>
            <S.Label>주의사항</S.Label>
            <S.Textarea
              rows={2}
              placeholder="예) 음식물 반입 금지, 사용 후 정리 필수"
              value={caution}
              onChange={(e) => setCaution(e.target.value)}
            />
          </S.Field>

          {/* 썸네일 이미지 */}
          <S.Field>
            <S.Label>썸네일 이미지 <S.Required>*</S.Required></S.Label>
            <S.ImageUploadBox hasError={!!errors.thumbnail}>
              <input type="file" accept="image/*" onChange={(e) => {
                setThumbnailFile(e.target.files?.[0] ?? null);
                setErrors((p) => ({ ...p, thumbnail: undefined }));
              }} />
              {thumbnailFile ? (
                <>
                  <S.UploadIcon>🖼️</S.UploadIcon>
                  <S.UploadedName>{thumbnailFile.name}</S.UploadedName>
                </>
              ) : (
                <>
                  <S.UploadIcon>📷</S.UploadIcon>
                  <S.UploadText>클릭해서 썸네일 이미지 선택</S.UploadText>
                </>
              )}
            </S.ImageUploadBox>
            {errors.thumbnail && <S.FieldError>{errors.thumbnail}</S.FieldError>}
          </S.Field>

          {/* 추가 이미지 */}
          <S.Field>
            <S.Label>추가 이미지</S.Label>
            {extraFiles.length > 0 && (
              <S.MultiImageList>
                {extraFiles.map((file, i) => (
                  <S.MultiImageRow key={i}>
                    <S.MultiImageName>{file.name}</S.MultiImageName>
                    <S.RemoveImageBtn onClick={() => removeExtraFile(i)}>삭제</S.RemoveImageBtn>
                  </S.MultiImageRow>
                ))}
              </S.MultiImageList>
            )}
            <S.AddImageBtn>
              <input
                ref={extraInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleExtraFiles}
              />
              + 이미지 추가
            </S.AddImageBtn>
          </S.Field>

          {/* 운영 시간 */}
          <S.Field>
            <S.SectionLabel>운영 시간</S.SectionLabel>
            <S.OpTimeList>
              {opTimes.map((t, i) => (
                <S.OpTimeRow key={i}>
                  <S.Field>
                    {i === 0 && <S.Label>구분명</S.Label>}
                    <S.Input
                      placeholder="예) 평일"
                      value={t.name}
                      onChange={(e) => setOpTime(i, "name", e.target.value)}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label>시작 시간</S.Label>}
                    <TimeSelect
                      value={t.startTime}
                      onChange={(v) => setOpTime(i, "startTime", v)}
                      height={44}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label>종료 시간</S.Label>}
                    <TimeSelect
                      value={t.endTime}
                      onChange={(v) => setOpTime(i, "endTime", v)}
                      height={44}
                    />
                  </S.Field>
                  <S.Field>
                    {i === 0 && <S.Label style={{ opacity: 0 }}>-</S.Label>}
                    <S.RemoveOpBtn
                      onClick={() => removeOpTime(i)}
                      disabled={opTimes.length === 1}
                    >
                      ✕
                    </S.RemoveOpBtn>
                  </S.Field>
                </S.OpTimeRow>
              ))}
            </S.OpTimeList>
            <S.AddOpBtn onClick={addOpTime} style={{ marginTop: 10 }}>
              + 운영 시간 추가
            </S.AddOpBtn>
          </S.Field>

          {/* 예약 정책 */}
          <S.Field>
            <S.SectionLabel>예약 정책</S.SectionLabel>
            <S.CheckboxRow>
              <S.CheckboxInput
                type="checkbox"
                id="reservable"
                checked={reservable}
                onChange={(e) => setReservable(e.target.checked)}
              />
              <label htmlFor="reservable">예약 가능</label>
            </S.CheckboxRow>
            {reservable && (
              <>
                <S.AvailableTimeList>
                  {availableTimes.map((t, i) => (
                    <S.AvailableTimeRow key={i}>
                      <TimeSelect
                        value={t.startTime}
                        onChange={(v) => setAvailableTime(i, "startTime", v)}
                        height={44}
                      />
                      <S.TimeSep>~</S.TimeSep>
                      <TimeSelect
                        value={t.endTime}
                        onChange={(v) => setAvailableTime(i, "endTime", v)}
                        height={44}
                      />
                      <S.RemoveOpBtn onClick={() => removeAvailableTime(i)}>
                        ✕
                      </S.RemoveOpBtn>
                    </S.AvailableTimeRow>
                  ))}
                </S.AvailableTimeList>
                <S.AddOpBtn onClick={addAvailableTime} style={{ marginTop: 8 }}>
                  + 예약 가능 시간대 추가
                </S.AddOpBtn>
              </>
            )}
          </S.Field>

          {/* 건물 */}
          <S.Field>
            <S.Label>건물</S.Label>
            <S.Select
              value={buildingId || ""}
              onChange={(e) => setBuildingId(Number(e.target.value))}
            >
              <option value="">선택하세요</option>
              {buildings.map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </S.Select>
          </S.Field>

          {/* 테마 */}
          <S.Field>
            <S.SectionLabel>테마</S.SectionLabel>
            {themes.length === 0 ? (
              <S.EmptyNote>등록된 테마가 없습니다.</S.EmptyNote>
            ) : (
              <S.ThemeGrid>
                {themes.map((theme) => (
                  <S.ThemeChip
                    key={theme.id}
                    isSelected={themeIds.includes(theme.id)}
                    onClick={() => toggleTheme(theme.id)}
                  >
                    {theme.name}
                  </S.ThemeChip>
                ))}
              </S.ThemeGrid>
            )}
          </S.Field>

          {submitError && <S.SubmitError>{submitError}</S.SubmitError>}

        </S.DrawerBody>

        {/* 푸터 */}
        <S.DrawerFooter>
          <S.CancelButton onClick={onClose}>취소</S.CancelButton>
          <S.SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "등록 중..." : "시설 추가"}
          </S.SubmitButton>
        </S.DrawerFooter>

      </S.Drawer>
    </S.Overlay>
  );
};

export default AdminFacilityDrawer;
